import { Component, ChangeDetectionStrategy, computed, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

export type BannerTone = 'amber' | 'red' | 'info' | 'success' | 'neutral';

/** Look of a declarative banner action: navy fill (the one main action), outlined, or red fill. */
export type BannerActionTone = 'primary' | 'outline' | 'danger';

/** One button of the banner's action row, rendered by the banner itself (see `actions`). */
export interface BannerAction {
  label: string;
  /** Material Symbols name shown before the label. */
  icon?: string;
  /** `primary` when omitted. */
  tone?: BannerActionTone;
  /**
   * Not available right now: `aria-disabled` (the button keeps its focus, WCAG 2.4.3) and a click
   * emits nothing. Never the native `disabled`, which would drop the focus to the page.
   */
  disabled?: boolean;
  /** A request of this action is out: `aria-busy` and the icon spins. */
  busy?: boolean;
}

/**
 * Inline notice bar — option holds ("Held on option until …", red once expired), the missing
 * IBAN warning on Banking, the admin "Viewing the product as …" / "Editing as …" bar in the shell
 * (handoff §12, §15, §17; F9 write mode). Body is projected; actions sit at the end of the row
 * (wrapping under the text on narrow widths) and come two ways: declaratively through `actions`
 * (pill buttons the banner renders itself, `actionClick` emits the index — what the shell slot
 * uses, since `ShellBannerService` only carries data) or projected in
 * `<ng-container bannerActions>` for a screen that owns its own buttons.
 *
 * a11y: red is `role="alert"` (assertive), every other tone `role="status"` (polite), so a
 * banner that appears after an action is announced without stealing focus. Declarative actions
 * are native buttons (Tab / Enter / Space), 44 px tall at ≤ 860 px.
 */
@Component({
  selector: 'ext-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, TranslateModule],
  template: `
  @if (!isDismissed()) {
    <div class="banner" [class]="'banner tone-' + tone()" [attr.role]="tone() === 'red' ? 'alert' : 'status'">
      <mat-icon class="banner-icon" aria-hidden="true">{{ iconName() }}</mat-icon>
      <div class="banner-text">
        @if (title()) { <strong class="banner-title">{{ title() }}</strong> }
        <span class="banner-body"><ng-content /></span>
      </div>
      <div class="banner-actions">
        <!-- Tracked by index so a relabelled action keeps its element (and the focus on it). -->
        @for (action of actions(); track $index) {
          <button type="button" class="banner-action" [class]="'banner-action action-' + (action.tone ?? 'primary')"
                  [attr.aria-disabled]="action.disabled ? 'true' : null" [attr.aria-busy]="action.busy ? 'true' : null"
                  (click)="!action.disabled && actionClick.emit($index)">
            @if (action.icon) { <mat-icon aria-hidden="true" [class.banner-spin]="action.busy">{{ action.icon }}</mat-icon> }
            <span>{{ action.label }}</span>
          </button>
        }
        <ng-content select="[bannerActions]" />
      </div>
      @if (dismissible()) {
        <button type="button" class="banner-close" (click)="dismiss()" [attr.aria-label]="'shared.banner.dismiss' | translate">
          <mat-icon aria-hidden="true">close</mat-icon>
        </button>
      }
    </div>
  }`,
  styles: [`
    :host { display: block; }
    .banner {
      display: flex; align-items: center; flex-wrap: wrap; gap: 10px 12px;
      padding: 12px 15px; border: 1px solid; border-radius: var(--ext-radius-lg);
      font-size: 13px; line-height: 1.5; color: var(--ext-text-default);
    }
    .banner-icon { flex-shrink: 0; font-size: 18px; width: 18px; height: 18px; }
    .banner-text { flex: 1 1 200px; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
    .banner-title { font-weight: 700; }
    .banner-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
    .banner-actions:empty { display: none; }
    .banner-close {
      display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px;
      margin-inline-start: auto; border: none; border-radius: var(--ext-radius-sm); background: transparent;
      color: inherit; cursor: pointer;
    }
    .banner-close mat-icon { font-size: 18px; width: 18px; height: 18px; }
    .banner-close:hover { background: rgba(13, 27, 46, .08); }
    .banner-close:focus-visible { outline: 2px solid var(--ext-border-focus); outline-offset: 2px; }

    /* Declarative actions: pills in the banner's own row. The fill is the one main action; the
       outline is the secondary one (a mode toggle); danger is a red fill. Only transform/opacity
       move (motion dial 2). */
    .banner-action {
      display: inline-flex; align-items: center; justify-content: center; gap: 6px;
      min-height: 32px; padding: 4px 12px; border: 1px solid transparent; border-radius: var(--ext-radius-pill);
      font-family: inherit; font-size: 12.5px; font-weight: 700; line-height: 1.2; white-space: nowrap; cursor: pointer;
      transition: transform var(--ext-motion-fast) var(--ext-ease-out), opacity var(--ext-motion-fast) var(--ext-ease-out);
    }
    .banner-action:active { transform: scale(.97); transition-timing-function: var(--ext-ease-in); }
    .banner-action:hover { opacity: .88; }
    .banner-action:focus-visible { outline: 2px solid var(--ext-border-focus); outline-offset: 2px; }
    .banner-action mat-icon { font-size: 16px; width: 16px; height: 16px; }
    .banner-action[aria-disabled='true'] { opacity: .6; cursor: default; transform: none; }
    .banner-spin { animation: banner-spin 1s linear infinite; }
    @keyframes banner-spin { to { transform: rotate(360deg); } }
    @media (prefers-reduced-motion: reduce) { .banner-spin { animation: none; } }
    .action-primary { background: var(--ext-navy-800); border-color: var(--ext-navy-800); color: var(--ext-surface-card); }
    .action-outline { background: var(--ext-surface-card); border-color: var(--ext-navy-300); color: var(--ext-navy-700); }
    .action-outline:hover { opacity: 1; border-color: var(--ext-navy-600); background: var(--ext-navy-50); }
    .action-danger { background: var(--ext-danger-500); border-color: var(--ext-danger-500); color: var(--ext-surface-card); }
    .tone-red .action-outline { border-color: var(--ext-danger-300); color: var(--ext-danger-700); }
    .tone-red .action-outline:hover { border-color: var(--ext-danger-500); background: var(--ext-danger-100); }
    @media (max-width: 860px) {
      .banner-close { width: 44px; height: 44px; }
      .banner-action { min-height: 44px; padding: 8px 16px; }
    }

    .tone-amber   { background: var(--ext-amber-50);   border-color: var(--ext-amber-200);   }
    .tone-amber .banner-icon   { color: var(--ext-amber-600); }
    .tone-red     { background: var(--ext-danger-50);  border-color: var(--ext-danger-200);  color: var(--ext-danger-700); }
    .tone-red .banner-icon     { color: var(--ext-danger-500); }
    .tone-info    { background: var(--ext-info-50);    border-color: var(--ext-info-100);    }
    .tone-info .banner-icon    { color: var(--ext-info-700); }
    .tone-success { background: var(--ext-success-50); border-color: var(--ext-success-100); }
    .tone-success .banner-icon { color: var(--ext-success-700); }
    .tone-neutral { background: var(--ext-surface-sunken); border-color: var(--ext-border); }
    .tone-neutral .banner-icon { color: var(--ext-text-muted); }
  `],
})
export class BannerComponent {
  tone = input<BannerTone>('info');
  /** Material Symbols name; defaults per tone (schedule / warning / info / check_circle / info). */
  icon = input<string>('');
  title = input<string>('');
  dismissible = input(false);
  /** Buttons the banner renders itself, in order; `actionClick` emits the clicked one's index. */
  actions = input<BannerAction[]>([]);

  dismissed = output<void>();
  actionClick = output<number>();

  /** Local only: a dismissed banner stays hidden until the component is recreated. */
  readonly isDismissed = signal(false);

  iconName = computed(() => this.icon() || DEFAULT_ICON[this.tone()]);

  dismiss(): void {
    this.isDismissed.set(true);
    this.dismissed.emit();
  }
}

const DEFAULT_ICON: Record<BannerTone, string> = {
  amber: 'schedule', red: 'warning', info: 'info', success: 'check_circle', neutral: 'info',
};
